import Sortable from "sortablejs";
import tippy from "tippy.js";
import { debounce } from "lodash-es";
import Rails from "@rails/ujs";

import { onViewLoad } from "~/jose_modern/on_view_load.js";
import { once, onceEach } from "~/jose_modern/once.js";
import { findPartials, findPartial } from "~/jose_modern/replace_partial.js";
import { Confirm } from "~/jose_modern/confirm.js";
import {
  userSearchesPath,
  userSearchSelectionsPath,
  distributeYieldRecipePath,
  optOutDuplicateIngredientWarningUserPath,
  optOutSubrecipeWarningUserPath,
  optOutVerificationRequestUserPath,
} from "routes.js";
import { unitWeights } from "~/recipal-app/controllers/shared/constants.js.erb";

import "./collapsed_table.js";

import "./collapsed_table.scss";
import "./recipes_edit.scss";
import { appendScript, requestJson } from "../shared/requests.js";
import { reloadIfLocked } from "./lock_response.js";
import { safeSubmitForm } from "jose_modern/auto_submit.js";
import { trackEvent } from "jose_modern/analytics.js";
import { bindJumpstart } from "./jumpstart.js";
import { initializeIngredientSearch } from "./ingredient_search.js";
import { initNameModal } from "./recipe_name_modal.js";

const clearConfirmAttributes = (selector) => {
  document.querySelectorAll(selector).forEach((element) => {
    delete element.dataset.confirm;
    delete element.dataset.confirmYes;
    delete element.dataset.confirmNo;
    delete element.dataset.confirmHtml;
    delete element.dataset.confirmAskAgain;
    delete element.dataset.confirmAskId;
  });
};

/* Posts the new RecipeIngredient order to the backend. */
const syncOrder = async (tbody) => {
  const newOrder = Array.from(
    findPartials("recipes/recipe_ingredient_table_row", { element: tbody })
  ).map((tr) => +tr.dataset.instance);

  const { response } = await requestJson("/recipe_ingredients/set_order", {
    method: "POST",
    body: JSON.stringify({ recipe_ingredients: newOrder }),
  });

  reloadIfLocked(response);
};

/* Enable drag-ordering on the body */
const makeDraggable = (tbody) => {
  Sortable.create(tbody, {
    handle: ".drag-handle",
    onEnd: () => syncOrder(tbody),
  });
};

/* Catch sort events and report them to the backend. */
const makeSortable = (table) => {
  onceEach([table], "recipes-ingredient-table-after-sort", () => {
    table.addEventListener("afterSort", () => syncOrder(table.querySelector("tbody")));
  });
};

const initializeTable = (container) => {
  onceEach(
    findPartials("recipes/recipe_ingredient_table_rows", { element: container }),
    "recipes-ingredient-table",
    (tbody) => {
      makeDraggable(tbody);
      makeSortable(tbody.closest("table"));
    }
  );

  // Prevent dropdown clicks from reaching Tablesort
  document
    .querySelectorAll("#recipe-ingredient-totals-type, #recipe-ingredient-totals-type option")
    .forEach((element) => {
      element.addEventListener("click", (event) => {
        event.stopPropagation();
      });
    });

  const decimalFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 4,
    useGrouping: false,
  });
  const percentFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });

  document.querySelector("#recipe-ingredient-totals-type").addEventListener("change", (event) => {
    // Weight type (g, kg, %, etc...)
    const type = event.target.value;

    const totalEl = document.querySelector("#recipe-weight");
    const totalWeight = totalEl.dataset["totalGrams"];

    // Individual ingredient rows
    const weightContainers = document.querySelectorAll("td.recipe-ingredient-weight");

    if (type == "percent") {
      totalEl.textContent = "100%";

      weightContainers.forEach((el) => {
        const gramWeight = Number(el.dataset["sort"]);

        // non-food ingredients don't have <input> they have a span with a dash
        const input = el.querySelector("input");
        if (input) {
          input.value = percentFormatter.format((gramWeight / totalWeight) * 100, 2) + "%";
        }
      });
    } else {
      const factor = unitWeights[type];
      totalEl.textContent = decimalFormatter.format(totalWeight / factor, 4);

      weightContainers.forEach((el) => {
        const gramWeight = Number(el.dataset["sort"]);

        // non-food ingredients don't have <input> they have a span with a dash
        const input = el.querySelector("input");
        if (input) {
          input.value = decimalFormatter.format(gramWeight / factor, 4);
        }
      });
    }
  });
};

const subrecipeWarning = async () => {
  const message = `
    <h3>You Are Editing a Subrecipe</h3>

    <p>You previously created a custom ingredient from this recipe.
    Any changes you make will be passed through to that custom ingredient as well as the
    other recipes the custom ingredient is a part of.

    <p>If you didn't mean to make this a subrecipe, you can delete the custom ingredient
    from your Dashboard under the Custom Ingredients section.
  `;

  const result = await Confirm.confirm(message, {
    askAgain: true,
    cancel: false,
    confirm: "OK, Got It!",
    html: true,
  });

  if (result && result.askAgain) {
    fetch(optOutSubrecipeWarningUserPath(window.currentUser.id), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({}),
    });
  }
};

const searchSession = { token: null, searchId: null, selected: false };

const recordSearchSelection = (el) => {
  if (!searchSession.searchId) return;

  fetch(userSearchSelectionsPath(window.currentUser.id, searchSession.searchId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    body: JSON.stringify({
      search_selection: {
        selected_id: el.dataset.ingredientId,
        position: el.dataset.resultPosition,
      },
    }),
  }).catch(() => {});

  searchSession.selected = true;
};

/* We increment a hidden field in the form each time a key is pressed.  This allows
   autocomplete responses to arrive out-of-order, and stale responses to be ignored.
*/
const initializeSearch = (container) => {
  const postSearchAfterMs = 1000;
  const analyticsAfterMs = 2000;
  const slowChars = 3;
  const slowDebounceTimeoutMs = 300;
  const fastDebounceTimeoutMs = 100;

  onceEach(container.querySelectorAll("#search-form"), "search-form", (searchForm) => {
    const input = searchForm.querySelector("input[type=search]");
    const maxLength = +input.getAttribute("maxlength") ?? 255;

    const postToSearch = debounce(() => {
      const query = input.value.trim().substr(0, maxLength);

      if (query.length === 0) {
        /* Don't report empty search terms */
        return;
      }

      const count = +findPartial("recipes/search_results").dataset.resultCount;

      if (!searchSession.token || searchSession.selected) {
        searchSession.token = crypto.randomUUID();
        searchSession.selected = false;
      }

      fetch(userSearchesPath(window.currentUser.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          search: {
            query,
            results: count,
            session_token: searchSession.token,
          },
        }),
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((search) => {
          if (search) {
            searchSession.searchId = search.id;
          }
        });
    }, postSearchAfterMs);

    const trackAnalytics = debounce(() => trackEvent("search_ingredient"), analyticsAfterMs);

    let lastSubmittedQuery = input.value.trim();
    let pendingSearch = null;

    const performSearch = () => {
      const query = input.value.trim();
      if (query === lastSubmittedQuery) return;
      lastSubmittedQuery = query;

      const sequenceElement = searchForm.querySelector("input[name=sequence]");
      sequenceElement.value = +sequenceElement.value + 1;

      if (safeSubmitForm(searchForm)) {
        trackAnalytics();
        postToSearch();
      }
    };

    input.addEventListener("input", () => {
      clearTimeout(pendingSearch);
      const waitMs =
        input.value.length <= slowChars ? slowDebounceTimeoutMs : fastDebounceTimeoutMs;
      pendingSearch = setTimeout(performSearch, waitMs);
    });
  });
};

const initializeGetStartedTooltip = () => {
  if (document.querySelector("[data-partial='recipes/recipe_ingredient_table_row']")) {
    // Don't show tooltip if the user already has ingredients
    return;
  }

  const target = getStartedTarget();

  const tooltip = tippy(target, {
    content: "Get started here",
    appendTo: document.body,
    trigger: "manual",
    theme: "recipal",
  });

  tooltip.show();
  target.addEventListener("input", () => {
    tooltip.destroy();
  });
};

const getStartedTarget = () => {
  return document.querySelector("#jumpstart") || document.querySelector("#search_field");
};

const initializeSearchResults = (container) => {
  onceEach(
    container.querySelectorAll(".add-ingredient-button"),
    "add-ingredient-analytics",
    (el) => {
      el.addEventListener("ajax:before", () => {
        trackEvent("add_ingredient_to_recipe", {
          search_filter: el.dataset.searchFilter,
          ingredient_origin: el.dataset.ingredientOrigin,
          page_number: el.dataset.pageNumber,
        });
        recordSearchSelection(el);
      });
    }
  );

  onceEach(container.querySelectorAll(".ingredient-row"), "ingredient-row-hover", (row) => {
    const addButton = row.querySelector(".add-ingredient-button");
    const otherButtons = row.querySelectorAll(
      ".preview-button, .add-recommended-button, .recommended-ingredient-link"
    );
    if (!addButton) return;

    row.addEventListener("mouseenter", () => {
      if (!addButton) return;
      addButton.classList.add("is-hovered");
    });

    row.addEventListener("mouseleave", () => {
      if (!addButton) return;
      addButton.classList.remove("is-hovered");
    });

    otherButtons.forEach((button) => {
      button.addEventListener("mouseenter", () => {
        addButton.classList.remove("is-hovered");
        addButton.classList.add("is-outlined");
      });

      button.addEventListener("mouseleave", () => {
        addButton.classList.remove("is-outlined");
        // Restore add button hover state if still hovering the row
        if (row.matches(":hover")) {
          addButton.classList.add("is-hovered");
        }
      });
    });

    // Clicking the row triggers the add button
    row.addEventListener("click", (e) => {
      if (
        !e.target.closest(".preview-button, .add-ingredient-button, .recommended-ingredient-link")
      ) {
        e.preventDefault();
        Rails.fire(addButton, "click");
      }
    });
  });
};

const initializePackageHelper = (container) => {
  const button = container.querySelector("#recipe_set_from_ingredients");
  if (!button) {
    return;
  }

  once(button, "set-from-ingredients", async (button) => {
    button.addEventListener("click", async (e) => {
      e.preventDefault();

      const { recipeId } = button.dataset;

      const response = await fetch(distributeYieldRecipePath(recipeId), {
        method: "POST",
        headers: { Accept: "application/javascript" },
      });

      if (response.status !== 200) {
        return;
      }

      const script = await response.text();

      appendScript(script);
    });
  });
};

function initializeJumpstart(container) {
  onceEach(container.querySelectorAll(".jumpstart"), "jumpstart", (jumpstart) =>
    bindJumpstart(jumpstart)
  );
}

function initializeServingSize(container) {
  const el = container.querySelector("#serving-size-calculator-button");
  if (!el) return;

  once(el, "serving-size", (btn) => {
    btn.addEventListener("click", (evt) => {
      evt.preventDefault();
      document.dispatchEvent(new CustomEvent("open-serving-size-calculator"));
    });
  });
}

/* All "+N" buttons share one expanded/collapsed state, stored as a class on the
   table partial wrapper so it survives full-table replacements. */
function initializeTagsOverflowToggle(container) {
  onceEach(
    container.querySelectorAll("[data-tags-overflow-toggle]"),
    "tags-overflow-toggle",
    (button) => {
      button.addEventListener("click", () => {
        button
          .closest("[data-partial='recipes/recipe_ingredient_table']")
          ?.classList.toggle("tags-expanded");
      });
    }
  );
}

const scan = (container) => {
  initializeSearch(container);
  initializeSearchResults(container);
  initializeTable(container);
  initializePackageHelper(container);
  initializeJumpstart(container);
  initializeIngredientSearch(container);
  initializeServingSize(container);
  initializeTagsOverflowToggle(container);
};

onViewLoad("recipes#edit", () => {
  scan(document);
  document.addEventListener("partial:replace", (ev) => scan(ev.container));
  initNameModal();

  initializeGetStartedTooltip();

  document.addEventListener("rails-ujs:confirm", (ev) => {
    if (ev.element.dataset.confirmAskId === "unverified-ingredient" && ev.askAgain) {
      fetch(optOutVerificationRequestUserPath(window.currentUser.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({}),
      }).then((response) => {
        if (response.ok) {
          clearConfirmAttributes(
            '.add-ingredient-button[data-confirm-ask-id="unverified-ingredient"]'
          );
        }
      });
    }

    if (ev.element.dataset.confirmAskId === "duplicate-ingredient" && ev.askAgain) {
      window._optedOutDuplicateIngredientWarning = true;
      fetch(optOutDuplicateIngredientWarningUserPath(window.currentUser.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({}),
      }).then((response) => {
        if (response.ok) {
          clearConfirmAttributes(
            '.add-ingredient-button[data-confirm-ask-id="duplicate-ingredient"]'
          );
        }
      });
    }
  });

  if (document.querySelector("[data-subrecipe-warning]")) {
    subrecipeWarning();
  }
});
